import React, { useState } from 'react';
import { X, Copy, Check, ExternalLink, Share2, Calendar, Hash, Image, MessageSquare, Tag, Eye } from 'lucide-react';
import { CampaignPost } from '../types/calendar';

interface PostDetailModalProps {
  post: CampaignPost | null;
  onClose: () => void;
  onUpdateStatus?: (postId: string, newStatus: string) => void;
  onSharePost?: (post: CampaignPost) => void;
  isReadOnly?: boolean;
}

export const PostDetailModal: React.FC<PostDetailModalProps> = ({
  post,
  onClose,
  onUpdateStatus,
  onSharePost,
  isReadOnly = false,
}) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  if (!post) return null;

  const handleCopy = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const getPlatformClass = (platform: string) => {
    const p = platform.toLowerCase();
    if (p.includes('instagram')) return 'platform-instagram';
    if (p.includes('facebook')) return 'platform-facebook';
    if (p.includes('tiktok')) return 'platform-tiktok';
    if (p.includes('pinterest')) return 'platform-pinterest';
    if (p.includes('youtube')) return 'platform-youtube';
    return '';
  };

  const statusOptions = ['Idea', 'Draft', 'Approved', 'Scheduled', 'Published'];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(15, 23, 42, 0.6)',
      backdropFilter: 'blur(6px)',
      zIndex: 100,
      display: 'flex',
      justifyContent: 'flex-end',
      animation: 'fadeIn 0.2s ease-out',
    }}>
      <div style={{
        width: '100%',
        maxWidth: '680px',
        height: '100%',
        background: 'var(--bg-surface)',
        boxShadow: 'var(--shadow-xl)',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        borderLeft: '1px solid var(--border-color)',
      }}>
        {/* Header Bar */}
        <div style={{
          padding: '1.25rem 1.5rem',
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          background: 'var(--bg-surface)',
          zIndex: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <span style={{ 
              fontFamily: 'var(--font-heading)', 
              fontWeight: 700, 
              fontSize: '1.1rem',
              color: 'var(--accent-primary)',
            }}>
              {post.postId}
            </span>
            <span className={`platform-badge ${getPlatformClass(post.platform)}`}>
              {post.platform}
            </span>
            <span className="badge" style={{ background: 'var(--bg-primary)', color: 'var(--text-muted)', border: '1px solid var(--border-color)' }}>
              {post.phase}
            </span>
            <span className="badge" style={{ background: 'var(--bg-primary)', color: 'var(--text-muted)', border: '1px solid var(--border-color)' }}>
              {post.week}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {onSharePost && (
              <button
                onClick={() => onSharePost(post)}
                style={{
                  background: 'var(--accent-glow)',
                  border: '1px solid var(--accent-primary)',
                  borderRadius: '8px',
                  padding: '0.35rem 0.65rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  color: 'var(--accent-primary)',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
                title="Share link to this post"
              >
                <Share2 size={14} />
                Share Link
              </button>
            )}

            <button
              onClick={onClose}
              style={{
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-muted)',
                cursor: 'pointer',
              }}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Content Body */}
        <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          
          {/* Metadata Row & Status Picker */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '1rem',
            padding: '1rem',
            borderRadius: '10px',
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-color)',
          }}>
            <div>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-light)', display: 'block' }}>Publish Date</span>
              <strong style={{ fontSize: '0.9rem', color: 'var(--text-main)' }}>{post.date} ({post.day})</strong>
            </div>
            <div>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-light)', display: 'block' }}>Format</span>
              <strong style={{ fontSize: '0.9rem', color: 'var(--text-main)' }}>{post.format || 'N/A'}</strong>
            </div>
            <div>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-light)', display: 'block' }}>Content Pillar</span>
              <strong style={{ fontSize: '0.9rem', color: 'var(--text-main)' }}>{post.contentPillar || 'N/A'}</strong>
            </div>
            <div>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-light)', display: 'block', marginBottom: '0.25rem' }}>
                Status {isReadOnly && '(Read-Only)'}
              </span>
              <select
                value={post.status}
                onChange={(e) => !isReadOnly && onUpdateStatus && onUpdateStatus(post.postId, e.target.value)}
                disabled={isReadOnly}
                title={isReadOnly ? 'Status cannot be changed in Read-Only Mode (Public URL)' : 'Change Status'}
                style={{
                  padding: '0.25rem 0.5rem',
                  borderRadius: '6px',
                  border: '1px solid var(--border-color)',
                  background: isReadOnly ? 'var(--bg-primary)' : 'var(--bg-surface)',
                  color: isReadOnly ? 'var(--text-muted)' : 'var(--text-main)',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: isReadOnly ? 'not-allowed' : 'pointer',
                  opacity: isReadOnly ? 0.75 : 1,
                }}
              >
                {statusOptions.map(st => (
                  <option key={st} value={st}>{st}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Hook Section */}
          {post.hook && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                <h3 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Hook / Headline
                </h3>
                <button
                  onClick={() => handleCopy(post.hook, 'hook')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: copiedField === 'hook' ? '#10b981' : 'var(--accent-primary)',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                  }}
                >
                  {copiedField === 'hook' ? <Check size={14} /> : <Copy size={14} />}
                  {copiedField === 'hook' ? 'Copied!' : 'Copy Hook'}
                </button>
              </div>
              <div style={{
                padding: '0.85rem 1rem',
                borderRadius: '8px',
                background: 'var(--bg-primary)',
                borderLeft: '4px solid var(--accent-primary)',
                fontSize: '0.95rem',
                fontWeight: 600,
                color: 'var(--text-main)',
              }}>
                "{post.hook}"
              </div>
            </div>
          )}

          {/* Caption Section */}
          {post.caption && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                <h3 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Caption
                </h3>
                <button
                  onClick={() => handleCopy(post.caption, 'caption')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: copiedField === 'caption' ? '#10b981' : 'var(--accent-primary)',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                  }}
                >
                  {copiedField === 'caption' ? <Check size={14} /> : <Copy size={14} />}
                  {copiedField === 'caption' ? 'Copied!' : 'Copy Caption'}
                </button>
              </div>
              <div style={{
                padding: '1rem',
                borderRadius: '8px',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                fontSize: '0.9rem',
                whiteSpace: 'pre-wrap',
                lineHeight: 1.6,
                color: 'var(--text-main)',
                maxHeight: '300px',
                overflowY: 'auto',
              }}>
                {post.caption}
              </div>
            </div>
          )}

          {/* Hashtags Section */}
          {post.hashtags && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                <h3 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Hashtags / Keywords
                </h3>
                <button
                  onClick={() => handleCopy(post.hashtags, 'hashtags')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: copiedField === 'hashtags' ? '#10b981' : 'var(--accent-primary)',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                  }}
                >
                  {copiedField === 'hashtags' ? <Check size={14} /> : <Copy size={14} />}
                  {copiedField === 'hashtags' ? 'Copied!' : 'Copy Hashtags'}
                </button>
              </div>
              <div style={{
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                fontSize: '0.85rem',
                color: 'var(--accent-primary)',
                lineHeight: 1.5,
              }}>
                {post.hashtags}
              </div>
            </div>
          )}

          {/* Visual & Asset Direction */}
          {post.visualDirection && (
            <div>
              <h3 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>
                Visual & Asset Direction
              </h3>
              <div style={{
                padding: '1rem',
                borderRadius: '8px',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                fontSize: '0.88rem',
                color: 'var(--text-main)',
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
              }}>
                {post.visualDirection}
              </div>
            </div>
          )}

          {/* Content To Create */}
          {post.contentToCreate && (
            <div>
              <h3 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>
                Content To Create
              </h3>
              <div style={{
                padding: '0.85rem 1rem',
                borderRadius: '8px',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                fontSize: '0.88rem',
                color: 'var(--text-main)',
              }}>
                {post.contentToCreate}
              </div>
            </div>
          )}

          {/* CTA & Link */}
          {(post.cta || post.link) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              {post.cta && (
                <div>
                  <h3 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>
                    Call To Action (CTA)
                  </h3>
                  <div style={{
                    padding: '0.75rem 1rem',
                    borderRadius: '8px',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-color)',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                  }}>
                    {post.cta}
                  </div>
                </div>
              )}
              {post.link && (
                <div>
                  <h3 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>
                    Destination Link
                  </h3>
                  <div style={{
                    padding: '0.75rem 1rem',
                    borderRadius: '8px',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-color)',
                    fontSize: '0.85rem',
                    color: 'var(--accent-primary)',
                    wordBreak: 'break-all',
                  }}>
                    {post.link}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          {post.notes && (
            <div>
              <h3 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem' }}>
                Notes
              </h3>
              <div style={{
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                fontSize: '0.85rem',
                fontStyle: 'italic',
                color: 'var(--text-muted)',
              }}>
                {post.notes}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
